// goal_walkthrough_view.js — pure HTML-generating module for the SMART goal
// walkthrough. No DOM access at import time so it can be tested under Node.js.
//
// The walkthrough guides users through:
// 1. Existing services (Continue/Change/Discontinue decisions)
// 2. Unmet needs (select which become goal candidates)
// 3. Shape each goal (SMART prompts)
// 4. Preview and save

/**
 * All user-facing copy in one place for easy editing/translation.
 */
export const COPY = {
  // Step titles
  step1Title: "Review Existing Services",
  step1Subtitle: "Before setting new goals, decide what happens to current services.",
  step2Title: "Unmet Needs",
  step2Subtitle: "Select needs that should become goals.",
  step3Title: "Shape Your Goal (SMART)",
  step3Subtitle: "Build a clear, actionable goal using the SMART framework.",
  step4Title: "Preview Your Goal",
  step4Subtitle: "Review how this goal will appear in the plan.",

  // Step 1: Service decisions
  serviceDecisionLabel: "What happens to this service?",
  continueLabel: "Continue",
  changeLabel: "Change",
  discontinueLabel: "Discontinue",
  futureSupportLabel: "Will this need still be there in the future?",
  futureSupportYes: "Yes",
  futureSupportNo: "No",
  futureSupportWhyPlaceholder: "Brief reason (optional)...",
  noServicesMessage: "No current services found. Proceed to the next step.",

  // Step 2: Unmet needs
  addAsGoalLabel: "Add as goal candidate",
  noNeedsMessage: "No unmet needs found. Proceed to the next step.",

  // Step 3: SMART prompts
  smartSpecificLabel: "Specific",
  smartSpecificHelper: "What exactly will be accomplished? Be clear and detailed.",
  smartSpecificExample: "Example: \"Learn to prepare a simple breakfast independently\"",
  smartSpecificPlaceholder: "Describe the specific outcome...",

  smartMeasurableLabel: "Measurable",
  smartMeasurableHelper: "How will we know when the goal is achieved?",
  smartMeasurableExample: "Example: \"Can make toast and cereal without assistance 3 times per week\"",
  smartMeasurablePlaceholder: "How will progress be measured...",

  smartAchievableLabel: "Achievable",
  smartAchievableHelper: "What supports and resources are needed?",
  smartAchievableExample: "Example: \"With OT support and visual step-by-step guides\"",
  smartAchievablePlaceholder: "Resources and supports needed...",

  smartRelevantLabel: "Relevant",
  smartRelevantHelper: "Why does this matter to the person? In their own words.",
  smartRelevantExample: "Example: \"I want to feel more independent in the morning\"",
  smartRelevantPlaceholder: "Why this goal matters...",

  smartTimeboundLabel: "Time-bound",
  smartTimeboundHelper: "When should this goal be achieved?",
  smartTimeboundExample: "Example: \"Within 6 months\"",
  smartTimeboundPlaceholder: "Timeline for achievement...",

  // Step 4: Preview
  saveToLibraryLabel: "Also save to my goal library",
  saveButtonLabel: "Save Goal",
  saveAndAddAnotherLabel: "Save & Add Another",

  // Navigation
  nextStepLabel: "Next",
  prevStepLabel: "Back",
  cancelLabel: "Cancel",
  skipStepLabel: "Skip",

  // Entry points
  startWalkthroughLabel: "Start goal walkthrough",
  refineWithWalkthroughLabel: "Refine with walkthrough",

  // Empty state
  noServicesContinueMsg: "No current services to review.",
  noNeedsContinueMsg: "No unmet needs to review.",
  noCandidatesMsg: "No goal candidates selected. Go back to select services or needs.",
}

/**
 * Escape HTML special characters to prevent XSS.
 * @param {*} v - value to escape
 * @returns {string} HTML-safe string
 */
export function esc(v) {
  return String(v == null ? "" : v)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
}

/**
 * Derive goal candidates from services based on their decision status.
 * Continue/Change -> candidate, Discontinue -> none
 *
 * @param {Array} services - array of service objects with `decision` field
 * @returns {Array} array of candidate objects
 */
export function candidatesFromServices(services) {
  if (!Array.isArray(services)) return []
  return services
    .filter(s => s && (s.decision === "continue" || s.decision === "change"))
    .map(s => ({
      id: s.id || `svc-${Date.now()}-${Math.random().toString(36).slice(2)}`,
      type: "service",
      sourceServiceId: s.id,
      text: s.text || s.name || "",
      vendor: s.vendor || null,
      vendorId: s.vendorId || null,
      decision: s.decision,
      futureSupport: s.futureSupport,
      futureSupportWhy: s.futureSupportWhy || "",
    }))
}

/**
 * Derive goal candidates from unmet needs.
 *
 * @param {Array} needs - array of need objects marked as candidates
 * @returns {Array} array of candidate objects
 */
export function candidatesFromNeeds(needs) {
  if (!Array.isArray(needs)) return []
  return needs
    .filter(n => n && n.isCandidate)
    .map(n => ({
      id: n.id || `need-${Date.now()}-${Math.random().toString(36).slice(2)}`,
      type: "need",
      sourceNeedId: n.id,
      text: n.text || n.description || "",
      vendor: n.vendor || null,
      vendorId: n.vendorId || null,
    }))
}

/**
 * Compose the full goal text from SMART fields.
 *
 * @param {object} smart - object with s, m, a, r, t fields
 * @returns {string} composed goal description
 */
export function composeGoalText(smart) {
  const s = smart || {}
  const parts = []

  if (s.s && s.s.trim()) {
    parts.push(s.s.trim())
  }
  if (s.m && s.m.trim()) {
    parts.push(`Progress will be measured by: ${s.m.trim()}`)
  }
  if (s.a && s.a.trim()) {
    parts.push(`Supports needed: ${s.a.trim()}`)
  }
  if (s.r && s.r.trim()) {
    parts.push(`This matters because: ${s.r.trim()}`)
  }
  if (s.t && s.t.trim()) {
    parts.push(`Timeline: ${s.t.trim()}`)
  }

  return parts.join(" ")
}

/**
 * Build HTML for the step indicator (1-2-3-4 dots).
 *
 * @param {number} currentStep - 1-based current step
 * @param {number} totalSteps - total number of steps
 * @returns {string} HTML string
 */
export function stepIndicatorHtml(currentStep, totalSteps = 4) {
  const dots = []
  for (let i = 1; i <= totalSteps; i++) {
    const activeClass = i === currentStep ? " goal-walkthrough__step-dot--active" : ""
    const completedClass = i < currentStep ? " goal-walkthrough__step-dot--completed" : ""
    dots.push(`<span class="goal-walkthrough__step-dot${activeClass}${completedClass}" data-step="${i}">${i}</span>`)
  }
  return `<div class="goal-walkthrough__step-indicator">${dots.join("")}</div>`
}

/**
 * Build HTML for a single service row in Step 1.
 *
 * @param {object} service - service object { id, text, vendor, vendorId }
 * @param {object} state - current decision state { decision, futureSupport, futureSupportWhy }
 * @returns {string} HTML string
 */
export function serviceRowHtml(service, state = {}) {
  const svc = service || {}
  const st = state || {}
  const decision = st.decision || ""
  const futureSupport = st.futureSupport || ""
  const futureSupportWhy = st.futureSupportWhy || ""
  const showFuture = decision === "continue" || decision === "change"

  const vendorName = svc.vendorName || (svc.vendor && svc.vendor.name) || ""

  return `
    <div class="goal-walkthrough__service-row" data-service-id="${esc(svc.id)}">
      <div class="goal-walkthrough__service-info">
        <span class="goal-walkthrough__service-name">${esc(svc.text || svc.name || "(unnamed)")}</span>
        ${vendorName ? `<span class="goal-walkthrough__service-vendor">${esc(vendorName)}</span>` : ""}
      </div>
      <div class="goal-walkthrough__service-decision">
        <label class="goal-walkthrough__decision-label">${esc(COPY.serviceDecisionLabel)}</label>
        <div class="goal-walkthrough__decision-btns">
          <button type="button" class="goal-walkthrough__decision-btn${decision === "continue" ? " is-selected" : ""}" data-decision="continue">${esc(COPY.continueLabel)}</button>
          <button type="button" class="goal-walkthrough__decision-btn${decision === "change" ? " is-selected" : ""}" data-decision="change">${esc(COPY.changeLabel)}</button>
          <button type="button" class="goal-walkthrough__decision-btn goal-walkthrough__decision-btn--danger${decision === "discontinue" ? " is-selected" : ""}" data-decision="discontinue">${esc(COPY.discontinueLabel)}</button>
        </div>
      </div>
      <div class="goal-walkthrough__future-support"${showFuture ? "" : " hidden"}>
        <label class="goal-walkthrough__future-label">${esc(COPY.futureSupportLabel)}</label>
        <div class="goal-walkthrough__future-btns">
          <button type="button" class="goal-walkthrough__future-btn${futureSupport === "yes" ? " is-selected" : ""}" data-future="yes">${esc(COPY.futureSupportYes)}</button>
          <button type="button" class="goal-walkthrough__future-btn${futureSupport === "no" ? " is-selected" : ""}" data-future="no">${esc(COPY.futureSupportNo)}</button>
        </div>
        <input type="text" class="goal-walkthrough__future-why" placeholder="${esc(COPY.futureSupportWhyPlaceholder)}" value="${esc(futureSupportWhy)}">
      </div>
    </div>`
}

/**
 * Build HTML for Step 1: Existing Services.
 *
 * @param {Array} services - array of current service objects
 * @param {object} decisions - map of serviceId -> { decision, futureSupport, futureSupportWhy }
 * @returns {string} HTML string
 */
export function step1Html(services, decisions = {}) {
  const svcList = Array.isArray(services) ? services : []
  const dec = decisions || {}

  if (svcList.length === 0) {
    return `
      <div class="goal-walkthrough__step" data-step="1">
        ${stepIndicatorHtml(1)}
        <h2 class="goal-walkthrough__title">${esc(COPY.step1Title)}</h2>
        <p class="goal-walkthrough__subtitle">${esc(COPY.step1Subtitle)}</p>
        <div class="goal-walkthrough__empty">${esc(COPY.noServicesContinueMsg)}</div>
      </div>`
  }

  const rows = svcList.map(s => serviceRowHtml(s, dec[s.id] || {})).join("")

  return `
    <div class="goal-walkthrough__step" data-step="1">
      ${stepIndicatorHtml(1)}
      <h2 class="goal-walkthrough__title">${esc(COPY.step1Title)}</h2>
      <p class="goal-walkthrough__subtitle">${esc(COPY.step1Subtitle)}</p>
      <div class="goal-walkthrough__services-list">${rows}</div>
    </div>`
}

/**
 * Build HTML for a single need row in Step 2.
 *
 * @param {object} need - need object { id, text, description }
 * @param {boolean} isCandidate - whether this need is selected as a candidate
 * @returns {string} HTML string
 */
export function needRowHtml(need, isCandidate = false) {
  const n = need || {}
  const vendorName = n.vendorName || (n.vendor && n.vendor.name) || ""

  return `
    <div class="goal-walkthrough__need-row" data-need-id="${esc(n.id)}">
      <label class="goal-walkthrough__need-check">
        <input type="checkbox" class="goal-walkthrough__need-checkbox"${isCandidate ? " checked" : ""}>
        <span class="goal-walkthrough__need-text">${esc(n.text || n.description || "(unnamed)")}</span>
        ${vendorName ? `<span class="goal-walkthrough__need-vendor">${esc(vendorName)}</span>` : ""}
      </label>
    </div>`
}

/**
 * Build HTML for Step 2: Unmet Needs.
 *
 * @param {Array} needs - array of unmet need objects
 * @param {Set|Array} selectedIds - IDs of needs selected as candidates
 * @returns {string} HTML string
 */
export function step2Html(needs, selectedIds = new Set()) {
  const needList = Array.isArray(needs) ? needs : []
  const selected = selectedIds instanceof Set ? selectedIds : new Set(selectedIds || [])

  if (needList.length === 0) {
    return `
      <div class="goal-walkthrough__step" data-step="2">
        ${stepIndicatorHtml(2)}
        <h2 class="goal-walkthrough__title">${esc(COPY.step2Title)}</h2>
        <p class="goal-walkthrough__subtitle">${esc(COPY.step2Subtitle)}</p>
        <div class="goal-walkthrough__empty">${esc(COPY.noNeedsContinueMsg)}</div>
      </div>`
  }

  const rows = needList.map(n => needRowHtml(n, selected.has(n.id))).join("")

  return `
    <div class="goal-walkthrough__step" data-step="2">
      ${stepIndicatorHtml(2)}
      <h2 class="goal-walkthrough__title">${esc(COPY.step2Title)}</h2>
      <p class="goal-walkthrough__subtitle">${esc(COPY.step2Subtitle)}</p>
      <div class="goal-walkthrough__needs-list">${rows}</div>
    </div>`
}

/**
 * Build HTML for a SMART prompt field.
 *
 * @param {string} key - field key (s, m, a, r, t)
 * @param {string} label - field label
 * @param {string} helper - helper text
 * @param {string} example - example text
 * @param {string} placeholder - input placeholder
 * @param {string} value - current value
 * @returns {string} HTML string
 */
export function smartFieldHtml(key, label, helper, example, placeholder, value = "") {
  return `
    <div class="goal-walkthrough__smart-field" data-smart-key="${esc(key)}">
      <label class="goal-walkthrough__smart-label">${esc(label)}</label>
      <p class="goal-walkthrough__smart-helper">${esc(helper)}</p>
      <p class="goal-walkthrough__smart-example">${esc(example)}</p>
      <textarea class="goal-walkthrough__smart-input" placeholder="${esc(placeholder)}" data-field="${esc(key)}">${esc(value)}</textarea>
    </div>`
}

/**
 * Build HTML for Step 3: SMART Goal shaping for a single candidate.
 *
 * @param {object} candidate - the goal candidate being shaped
 * @param {object} smart - current SMART values { s, m, a, r, t }
 * @param {number} candidateIndex - 0-based index of current candidate
 * @param {number} totalCandidates - total number of candidates
 * @param {boolean} showVendorPicker - whether to show vendor picker in Achievable
 * @returns {string} HTML string
 */
export function step3Html(candidate, smart = {}, candidateIndex = 0, totalCandidates = 1, showVendorPicker = true) {
  const c = candidate || {}
  const s = smart || {}
  const sourceLabel = c.type === "service" ? "Service" : "Need"
  const sourceText = c.text || ""

  // Pre-fill Specific with source text if not already set
  const specificValue = s.s != null ? s.s : sourceText

  const progress = totalCandidates > 1
    ? `<span class="goal-walkthrough__candidate-progress">Goal ${candidateIndex + 1} of ${totalCandidates}</span>`
    : ""

  return `
    <div class="goal-walkthrough__step" data-step="3">
      ${stepIndicatorHtml(3)}
      <h2 class="goal-walkthrough__title">${esc(COPY.step3Title)}</h2>
      <p class="goal-walkthrough__subtitle">${esc(COPY.step3Subtitle)}</p>
      ${progress}
      <div class="goal-walkthrough__candidate-source">
        <span class="goal-walkthrough__source-type">${esc(sourceLabel)}:</span>
        <span class="goal-walkthrough__source-text">${esc(sourceText)}</span>
      </div>
      <div class="goal-walkthrough__smart-fields">
        ${smartFieldHtml("s", COPY.smartSpecificLabel, COPY.smartSpecificHelper, COPY.smartSpecificExample, COPY.smartSpecificPlaceholder, specificValue)}
        ${smartFieldHtml("m", COPY.smartMeasurableLabel, COPY.smartMeasurableHelper, COPY.smartMeasurableExample, COPY.smartMeasurablePlaceholder, s.m || "")}
        <div class="goal-walkthrough__smart-field" data-smart-key="a">
          <label class="goal-walkthrough__smart-label">${esc(COPY.smartAchievableLabel)}</label>
          <p class="goal-walkthrough__smart-helper">${esc(COPY.smartAchievableHelper)}</p>
          <p class="goal-walkthrough__smart-example">${esc(COPY.smartAchievableExample)}</p>
          <textarea class="goal-walkthrough__smart-input" placeholder="${esc(COPY.smartAchievablePlaceholder)}" data-field="a">${esc(s.a || "")}</textarea>
          ${showVendorPicker ? '<div class="goal-walkthrough__vendor-picker-slot" data-controller="vendor-picker" data-vendor-picker-value-value="" data-vendor-picker-name-value="" data-vendor-picker-placeholder-value="Search or add a vendor..."></div>' : ""}
        </div>
        ${smartFieldHtml("r", COPY.smartRelevantLabel, COPY.smartRelevantHelper, COPY.smartRelevantExample, COPY.smartRelevantPlaceholder, s.r || "")}
        ${smartFieldHtml("t", COPY.smartTimeboundLabel, COPY.smartTimeboundHelper, COPY.smartTimeboundExample, COPY.smartTimeboundPlaceholder, s.t || "")}
      </div>
    </div>`
}

/**
 * Build HTML for Step 4: Preview.
 *
 * @param {object} candidate - the goal candidate
 * @param {object} smart - SMART values
 * @param {boolean} saveToLibrary - whether the library checkbox is checked
 * @param {number} candidateIndex - 0-based index
 * @param {number} totalCandidates - total candidates
 * @returns {string} HTML string
 */
export function step4Html(candidate, smart = {}, saveToLibrary = false, candidateIndex = 0, totalCandidates = 1) {
  const c = candidate || {}
  const composedText = composeGoalText(smart)
  const hasMoreCandidates = candidateIndex < totalCandidates - 1

  const progress = totalCandidates > 1
    ? `<span class="goal-walkthrough__candidate-progress">Goal ${candidateIndex + 1} of ${totalCandidates}</span>`
    : ""

  return `
    <div class="goal-walkthrough__step" data-step="4">
      ${stepIndicatorHtml(4)}
      <h2 class="goal-walkthrough__title">${esc(COPY.step4Title)}</h2>
      <p class="goal-walkthrough__subtitle">${esc(COPY.step4Subtitle)}</p>
      ${progress}
      <div class="goal-walkthrough__preview">
        <div class="goal-walkthrough__preview-heading">${esc(smart.s || c.text || "Goal")}</div>
        <div class="goal-walkthrough__preview-body">${esc(composedText)}</div>
      </div>
      <div class="goal-walkthrough__save-options">
        <label class="goal-walkthrough__library-check">
          <input type="checkbox" data-save-to-library${saveToLibrary ? " checked" : ""}>
          <span>${esc(COPY.saveToLibraryLabel)}</span>
        </label>
      </div>
      <div class="goal-walkthrough__save-actions">
        <button type="button" class="goal-walkthrough__save-btn" data-action="save">${esc(COPY.saveButtonLabel)}</button>
        ${hasMoreCandidates ? `<button type="button" class="goal-walkthrough__save-next-btn" data-action="save-next">${esc(COPY.saveAndAddAnotherLabel)}</button>` : ""}
      </div>
    </div>`
}

/**
 * Build HTML for navigation buttons at the bottom of each step.
 *
 * @param {number} currentStep - 1-based current step
 * @param {number} totalSteps - total steps
 * @param {boolean} canSkip - whether skip is allowed on this step
 * @returns {string} HTML string
 */
export function navButtonsHtml(currentStep, totalSteps = 4, canSkip = false) {
  const showBack = currentStep > 1
  const showNext = currentStep < totalSteps

  return `
    <div class="goal-walkthrough__nav">
      <button type="button" class="goal-walkthrough__cancel-btn" data-action="cancel">${esc(COPY.cancelLabel)}</button>
      <div class="goal-walkthrough__nav-right">
        ${showBack ? `<button type="button" class="goal-walkthrough__back-btn" data-action="back">${esc(COPY.prevStepLabel)}</button>` : ""}
        ${canSkip ? `<button type="button" class="goal-walkthrough__skip-btn" data-action="skip">${esc(COPY.skipStepLabel)}</button>` : ""}
        ${showNext ? `<button type="button" class="goal-walkthrough__next-btn" data-action="next">${esc(COPY.nextStepLabel)}</button>` : ""}
      </div>
    </div>`
}

/**
 * Build the complete walkthrough sheet HTML.
 *
 * @param {object} opts
 * @param {number} opts.step - current step (1-4)
 * @param {Array} opts.services - current services for step 1
 * @param {object} opts.serviceDecisions - service decision state
 * @param {Array} opts.needs - unmet needs for step 2
 * @param {Set|Array} opts.selectedNeedIds - selected need IDs
 * @param {Array} opts.candidates - goal candidates for steps 3-4
 * @param {number} opts.candidateIndex - current candidate index
 * @param {object} opts.smart - current SMART values
 * @param {boolean} opts.saveToLibrary - library checkbox state
 * @returns {string} HTML string
 */
export function walkthroughSheetHtml(opts = {}) {
  const step = opts.step || 1
  const services = opts.services || []
  const decisions = opts.serviceDecisions || {}
  const needs = opts.needs || []
  const selectedNeedIds = opts.selectedNeedIds || new Set()
  const candidates = opts.candidates || []
  const candidateIndex = opts.candidateIndex || 0
  const smart = opts.smart || {}
  const saveToLibrary = opts.saveToLibrary || false

  let stepContent = ""
  let canSkip = false

  switch (step) {
    case 1:
      stepContent = step1Html(services, decisions)
      canSkip = services.length === 0
      break
    case 2:
      stepContent = step2Html(needs, selectedNeedIds)
      canSkip = needs.length === 0
      break
    case 3:
      if (candidates.length === 0) {
        stepContent = `
          <div class="goal-walkthrough__step" data-step="3">
            ${stepIndicatorHtml(3)}
            <h2 class="goal-walkthrough__title">${esc(COPY.step3Title)}</h2>
            <div class="goal-walkthrough__empty">${esc(COPY.noCandidatesMsg)}</div>
          </div>`
      } else {
        const candidate = candidates[candidateIndex] || candidates[0]
        stepContent = step3Html(candidate, smart, candidateIndex, candidates.length)
      }
      break
    case 4:
      if (candidates.length === 0) {
        stepContent = `
          <div class="goal-walkthrough__step" data-step="4">
            ${stepIndicatorHtml(4)}
            <h2 class="goal-walkthrough__title">${esc(COPY.step4Title)}</h2>
            <div class="goal-walkthrough__empty">${esc(COPY.noCandidatesMsg)}</div>
          </div>`
      } else {
        const candidate = candidates[candidateIndex] || candidates[0]
        stepContent = step4Html(candidate, smart, saveToLibrary, candidateIndex, candidates.length)
      }
      break
    default:
      stepContent = step1Html(services, decisions)
  }

  // Step 4 has its own save buttons, so don't show nav next button
  const navHtml = step === 4 ? navButtonsHtml(step, 4, false) : navButtonsHtml(step, 4, canSkip)

  return `
<div class="goal-walkthrough goal-editor--sheet">
  <div class="goal-walkthrough__content">
    ${stepContent}
  </div>
  ${navHtml}
</div>`
}

/**
 * Build HTML for the entry point button.
 *
 * @param {string} variant - "start" or "refine"
 * @returns {string} HTML string
 */
export function entryButtonHtml(variant = "start") {
  const label = variant === "refine" ? COPY.refineWithWalkthroughLabel : COPY.startWalkthroughLabel
  const icon = variant === "refine" ? "edit-3" : "compass"
  return `<button type="button" class="goal-walkthrough__entry-btn" data-action="goal-walkthrough#open" data-variant="${esc(variant)}">
  <i data-lucide="${esc(icon)}" class="w-4 h-4"></i>
  <span>${esc(label)}</span>
</button>`
}
